<template>
  <div class="map">
    <div id="map" ref="map"></div>
    <div class="map__overlay">
      <slot name="overlay"></slot>
    </div>
    <div class="map__wrapper">
      <vb-button
        class="map__load-more"
        v-if="load_more !== null && map_markers.length > 1"
        @click="load_more()"
        :vb_options="{
          text: loading ? 'Caricamento' : 'Mostra più risultati',
          variant: 'primary',
          size: 'md',
          disabled: loading,
        }"
      >
        <loader v-if="loading" class="ml-2" />
      </vb-button>

      <slot></slot>
    </div>
  </div>
</template>

<script>
/**
 * TODO: la info-window può diventare un componente Vue: http://blog.olamisan.com/how-to-create-infowindow-component-in-vuejs
 */
import { number } from 'mathjs'

// Persiste zoom/center tra distruzione e ricostruzione del componente (v-if toggle)
let _saved_view = null

export default {
  name: "map-content-block",

  components: {
  },

  props: {
    load_more: {
      type: Function,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    load_info_window: {
      type: Boolean,
      default: false,
    },
    map_location: {
      type: Object,
      default: () => {
        return {
          lat: "0",
          lng: "0",
        }
      }
    },
    map_markers: {
      type: Array,
      default: () => { return [] }
    },
    map_style: {
      type: Object,
      default: () => { return {} }
    },
    map_info_window: {
      type: Object,
      default: () => { return {} }
    },
    preserve_view: {
      type: Boolean,
      default: false,
    },
    zoom_threshold: {
      type: Number,
      default: 12,
    }
  },

  data() {
    return {
      google_map: {},
      info_window: {},
      gm_markers: [],
      drag_debounce_timer: null,
      current_zoom: 10,
      is_first_render: true,
      gm_options: {
      scrollwheel: true,
      zoomControl: true,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false,
      disableDefaultUi: false,
      clickableIcons: false,  // Disabilita il click sui POI (città, luoghi, etc)
        options:{
          gestureHandling: 'greedy',  // zoom without ctrl+
          mapId: this.get_config('googleMaps.mapId') || undefined,
        },
        // map location
        zoom: 10,
        center: {
          lat: number(this.map_location.latitude || 0),
          lng: number(this.map_location.longitude || 0),
        },
      },
    }
  },

  computed: {   
    map() {
      return this.$refs.map
    },
  },

  created() {
  },

  beforeMount() {   
  },

  mounted() {
    const api_key = this.get_config('googleMaps.apikey', '')
    // https://dev.to/tareqnewazshahriar/how-to-add-google-map-api-script-in-a-vue-project-without-plugins-3ldi
    window.checkAndAttachMapScript = function(callback) {
      let scriptId = "map-api-script"
      let mapAlreadyAttached = !!document.getElementById(scriptId)
      if (mapAlreadyAttached) {
        if (window.google) callback()
      }
      else {
        window.mapApiInitialized = callback
        let script = document.createElement('script')
        script.id = scriptId
        script.src = `https://maps.googleapis.com/maps/api/js?key=${api_key}&libraries=places,geometry&callback=mapApiInitialized`
        script.async = true
        script.defer = true
        document.body.appendChild(script)
      }
      return mapAlreadyAttached
    }
    window.checkAndAttachMapScript(this.init_map)
  },

  watch: {
    map_markers: {
      handler() {
        // Se la mappa è già inizializzata, aggiorna i markers
        if (this.google_map && this.google_map.getCenter) {
          this.clear_markers()
          this.render_markers()
        }
      },
      deep: true,
    },
  },

  beforeDestroy() {
    if (this.info_window && this.info_window.close) this.info_window.close()
    if (this.drag_debounce_timer) {
      clearTimeout(this.drag_debounce_timer)
    }
    if (this.google_map && this.google_map.getZoom) {
      const center = this.google_map.getCenter()
      _saved_view = {
        zoom: this.google_map.getZoom(),
        center: { lat: center.lat(), lng: center.lng() },
      }
    }
  },

  methods: {

    init_map() {
      if (this.map == undefined) {
        // evito un potenziale undefined nelle $refs
        setTimeout(() => this.init_map(), 250)
      }
      else {
        this.render_map()
        this.render_markers()
      }
    },

    render_map() {
      this.google_map = new window.google.maps.Map(
        this.map,
        this.gm_options,
      )

      // Listener per drag della mappa
      this.google_map.addListener('dragend', () => {
        this.on_map_drag()
      })

      // Listener per zoom della mappa
      this.google_map.addListener('zoom_changed', () => {
        this.on_zoom_change()
      })
    },

    async render_markers(skip_fit_bounds = false) {
      let existing_markers = this.gm_markers.length
      const show_price = this.current_zoom >= this.zoom_threshold
      const {AdvancedMarkerElement} = await window.google.maps.importLibrary("marker")
      this.map_markers.forEach((item, index) => {
        if(index<existing_markers) return

        let marker_content
        // Usa marker con prezzo se zoom >= threshold e price_label disponibile
        if (show_price && item.price_label && item.color) {
          marker_content = this.create_price_marker_element(item.color, item.price_label)
        } else {
          marker_content = document.createElement('img')
          marker_content.src = item.icon.url
        }

        let marker = new AdvancedMarkerElement({
            map: this.google_map,
            zIndex: item.z_index ? item.z_index : 1,
            title: item.name,
            position: item.position,
            content: marker_content,
          })

        if (this.load_info_window) { // l'info window ora è gestito tramite prop
          marker.addEventListener('click', () => {
            if (this.info_window.close) this.info_window.close()
            let googleMapInfoWindow = new window.google.maps.InfoWindow({
              position: item.position,
              content: '<div id="info_window_id">'+item.content+'</div>',
              maxWidth: 260,
              pixelOffset: new window.google.maps.Size(
                this.map_info_window.offset ? this.map_info_window.offset.x : 0,
                this.map_info_window.offset ? this.map_info_window.offset.y : 0,
              )
            })
            googleMapInfoWindow.setMap(this.google_map)
            googleMapInfoWindow.close(this.google_map)
            window.google.maps.event.addListener(googleMapInfoWindow, 'domready', () => {
              let iw = document.getElementById('info_window_id')
              iw.addEventListener('click', () => { 
                this.$emit('item-clicked', {
                  index: index,
                  item: this.map_markers[index].item,
                })
              })
            })
            this.info_window = googleMapInfoWindow                      
            this.info_window.open(this.google_map)
          })
        }

        this.gm_markers[index] = marker
        this.gm_markers[index].setMap(this.google_map)
      })
      
      const is_first_render = this.is_first_render
      this.is_first_render = false

      if (skip_fit_bounds) return

      if (is_first_render && this.preserve_view && _saved_view) {
        // Ripristina zoom/center della sessione precedente (v-if toggle)
        this.google_map.setZoom(_saved_view.zoom)
        this.google_map.setCenter(_saved_view.center)
        _saved_view = null
      } else if (!this.preserve_view || is_first_render) {
        if (this.gm_markers.length <= 1) {
          this.google_map.setZoom(14)
          return
        }

        let bounds = new window.google.maps.LatLngBounds()
        this.map_markers.forEach(m => {
          bounds.extend(new window.google.maps.LatLng(m.position))
        })
        this.google_map.fitBounds(bounds)
      }
    },

    clear_markers() {
      if (this.info_window && this.info_window.close) this.info_window.close()
      this.gm_markers.forEach(marker => {
        if (marker && marker.setMap) {
          marker.setMap(null)
        }
      })
      this.gm_markers = []
    },

    on_map_drag() {
      if (this.drag_debounce_timer) {
        clearTimeout(this.drag_debounce_timer)
      }

      this.drag_debounce_timer = setTimeout(() => {
        const center = this.google_map.getCenter()
        this.$emit('map-drag', {
          latitude: center.lat(),
          longitude: center.lng(),
        })
      }, 300)
    },

    on_zoom_change() {
      const zoom = this.google_map.getZoom()
      const was_above_threshold = this.current_zoom >= this.zoom_threshold
      const is_above_threshold = zoom >= this.zoom_threshold

      this.current_zoom = zoom

      if (was_above_threshold !== is_above_threshold) {
        this.clear_markers()
        this.render_markers(true)
      }
    },

    // Crea elemento DOM per marker con prezzo
    create_price_marker_element(color, price) {
      const container = document.createElement('div')
      container.className = 'map-price-marker'

      const pill = document.createElement('div')
      pill.className = 'map-price-marker__pill'
      pill.textContent = price
      pill.style.setProperty('--marker-color', color)

      const arrow = document.createElement('div')
      arrow.className = 'map-price-marker__arrow'

      container.appendChild(pill)
      container.appendChild(arrow)
      return container
    },
  },
};
</script>

<style lang="scss" scoped>
  .map {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 100%;

    &__overlay {
      position: absolute;
      top: 1rem;
      right: 1rem;
      z-index: 10;
    }

    &__wrapper {
      margin-top: $grid-gutter-width;
    }

    &__load-more {
      display: flex;
      align-items: center;
      line-height: normal;
    }
  }

  #map {
    width: 100%;
    height: 100%;
  }
</style>

<!-- Stili per marker prezzo (non scoped perché elementi creati dinamicamente) -->
<style lang="scss">
  .map-price-marker {
    display: flex;
    flex-direction: column;
    align-items: center;

    &__pill {
      background: var(--marker-color);
      color: #ffffff;
      font-size: 12px;
      padding: 8px 12px;
      border-radius: 20px;
      border: 2px solid #F8F9FA;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
      white-space: nowrap;
    }

    &__arrow {
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-top: 6px solid #ffffff;
      margin-top: -1px;
      filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.3));
    }
  }
</style>